import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Clock, Weight, MapPinned } from "lucide-react";
import { CONTACTS } from "@/data/site";

const STATS = [
  { icon: Clock, label: "Response", value: "24/7 Fast Dispatch" },
  { icon: Weight, label: "Capacity", value: "Heavy Duty Rigs" },
  { icon: MapPinned, label: "Coverage", value: "Bloomington & IE Corridor" },
];

export default function Towing() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="towing" ref={ref} className="relative py-24 lg:py-32 bg-surface border-y border-steel overflow-hidden" data-testid="towing-section">
      <div className="absolute top-0 inset-x-0 h-1.5 hazard-stripes opacity-70" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-6"
        >
          <p className="font-mono text-xs uppercase tracking-widest text-racing font-semibold mb-3">/ 02 — Emergency Recovery</p>
          <h2 className="font-heading uppercase font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[0.95]">
            24/7 Heavy Duty <span className="text-racing italic">Towing</span> & Road Assistance
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-xl">
            Stranded on the freeway with a full load? GSX Diesel operates round-the-clock heavy
            towing equipment capable of hauling semi tractors, fully loaded trailers, construction
            vehicles, and buses. One call and the wrecker rolls.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-steel border border-steel">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                className="bg-carbon p-5"
                data-testid={`towing-stat-${i}`}
              >
                <s.icon className="w-5 h-5 text-hazard mb-3" />
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
                <p className="font-heading font-bold uppercase text-lg text-white mt-1">{s.value}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={CONTACTS.ray.tel}
              data-testid="towing-call-ray-button"
              className="group flex items-center gap-3 px-7 py-4 bg-racing text-white font-heading font-extrabold uppercase tracking-wide emergency-pulse hover:bg-[#E01F1F] transition-colors duration-300"
            >
              <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
              Ray — {CONTACTS.ray.phone}
            </a>
            <a
              href={CONTACTS.omar.tel}
              data-testid="towing-call-omar-button"
              className="flex items-center gap-3 px-7 py-4 border border-steel text-slate-200 font-heading font-bold uppercase tracking-wide hover:border-hazard hover:text-hazard transition-colors duration-300"
            >
              <Phone className="w-5 h-5" />
              Omar — {CONTACTS.omar.phone}
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative border border-steel overflow-hidden aspect-[4/3]">
            <motion.img
              style={{ y: imgY }}
              src="/assets/tow-volvo.jpg"
              alt="GSX Diesel heavy wrecker recovering a Freightliner Cascadia"
              className="w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-carbon/80 via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-hazard bg-carbon/80 backdrop-blur px-3 py-1.5 border border-steel">
                GSX Wrecker / On The Hook
              </span>
              <span className="font-heading font-black text-5xl text-white/15 select-none">24/7</span>
            </div>
          </div>
          <div className="absolute -bottom-3 -right-3 w-24 h-24 hazard-stripes opacity-60 -z-10" />
        </motion.div>
      </div>
      <div className="absolute bottom-0 inset-x-0 h-1.5 hazard-stripes opacity-70" />
    </section>
  );
}
